import { Controller } from "@hotwired/stimulus";
import { checkedParticipantLaborUnitsInputs } from "../helpers/assignment_participants.js";
import { consecutiveDateGroups, currentFieldTaskId, rowDateValues } from "../helpers/line_item_form.js";

// Quick-fill helpers under each assignment row (markup: QuantityHelpersComponent).
// Offers the selected lot's tree count / area for Cantidad and computes
// personas × días = jornales, written into the row as the count is typed. Sits next to line-item-rows on the rows root and
// re-evaluates after any interaction in the form; it only fills text, toggles
// classes and writes input values into server-rendered elements.
// Connects to data-controller="line-item-quantity-helpers"

// Everything that changes a row's inputs or visibility bubbles to the form as
// one of these. `click` covers the buttons that set values without an input
// event (add/remove row, apply-to-all, participant fill, custom dates).
const FORM_EVENTS = ["input", "change", "click", "field-task-changed"];

export default class extends Controller {
  static values = { taskQuantityKinds: Object, labels: Object };

  connect() {
    this.form = this.element.closest("form") || document;
    this.kinds = this.taskQuantityKindsValue;
    this.labels = this.labelsValue;
    // Always group thousands (es CLDR skips it below 10.000) to match Rails' number_to_delimited.
    this.numberFormat = new Intl.NumberFormat(document.documentElement.lang || "es", {
      maximumFractionDigits: 2,
      useGrouping: "always",
    });
    this.scheduleRefresh = () => this.#scheduleRefresh();
    FORM_EVENTS.forEach((name) => this.form.addEventListener(name, this.scheduleRefresh));
    this.#scheduleRefresh();
  }

  disconnect() {
    FORM_EVENTS.forEach((name) => this.form.removeEventListener(name, this.scheduleRefresh));
  }

  useFact(event) {
    const state = this.#rowState(this.#rowFor(event.currentTarget), this.#currentKind());
    const fact = event.currentTarget.closest("[data-lot-fact]").dataset.lotFact;
    const value = this.#factValue(state.lot, fact);
    if (value == null || !state.factsTarget) return;

    this.#setValue(state.factsTarget, value);
  }

  usePeople(event) {
    const row = this.#rowFor(event.currentTarget);
    const state = this.#rowState(row, this.#currentKind());
    const total = this.#laborUnitsFor(this.#peopleCount(row.querySelector("[data-people-helper]"), state), state.days);
    if (!state.laborUnitsTarget || total == null) return;

    const value = this.#laborUnitsValue(state, total);
    state.laborUnitsTarget.inputs.forEach((input) => this.#setValue(input, value));
  }

  // --- refresh ---------------------------------------------------------------

  #scheduleRefresh() {
    if (this.refreshScheduled) return;
    this.refreshScheduled = true;
    // Two frames: line-item-rows#addRow defers its own row setup by one.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.refreshScheduled = false;
        const kind = this.#currentKind();
        this.#rows().forEach((row) => this.#refreshRow(row, kind));
      }),
    );
  }

  #refreshRow(row, kind) {
    const strip = row.querySelector("[data-quantity-helpers]");
    if (!strip) return;

    const state = this.#rowState(row, kind);
    const showFacts = this.#renderFacts(strip.querySelector("[data-lot-facts]"), state);
    const showPeople = this.#renderPeople(strip.querySelector("[data-people-helper]"), state);
    strip.classList.toggle("hidden", !showFacts && !showPeople);
  }

  // --- state -----------------------------------------------------------------

  // What the selected task's quantity counts: "trees", "area", "labor_day" or null.
  #currentKind() {
    return this.kinds[currentFieldTaskId(this.form)] || null;
  }

  #rowState(row, kind) {
    const lotSelect = row.querySelector("select[data-lot-name]");
    const option = lotSelect?.options[lotSelect.selectedIndex];
    const lot = option?.value
      ? { name: option.text, trees: option.dataset.trees, area: option.dataset.area, noun: option.dataset.noun }
      : null;
    const laborDay = kind === "labor_day";
    const quantityInput = this.#visible(row.querySelector("input[data-quantity]"));
    const laborUnitsInput = this.#visible(row.querySelector("input[name$='[budgeted_labor_units]']"));
    const participantInputs = this.#checkedParticipantInputs(row);

    // Where jornales go for this row:
    //  - group with per-person jornales → each checked participant gets `days`
    //  - explicit Jornales input (labor_unit costing on a non-jornal task)
    //  - Cantidad itself when the task unit is jornal
    let laborUnitsTarget = null;
    if (participantInputs.length > 0) {
      laborUnitsTarget = { inputs: participantInputs, perPerson: true };
    } else if (laborUnitsInput) {
      laborUnitsTarget = { inputs: [laborUnitsInput], perPerson: false };
    } else if (laborDay && quantityInput) {
      laborUnitsTarget = { inputs: [quantityInput], perPerson: false };
    }

    return {
      lot,
      kind,
      factsTarget: laborDay ? null : quantityInput,
      laborUnitsTarget,
      days: this.#daysFor(rowDateValues(row, this.form)),
    };
  }

  #rows() {
    return Array.from(this.element.querySelectorAll("[data-line-item-rows-target='row']"));
  }

  #rowFor(element) {
    return element.closest("[data-line-item-rows-target='row']");
  }

  #visible(input) {
    return input && !input.closest(".hidden") ? input : null;
  }

  #checkedParticipantInputs(row) {
    const panel = row.querySelector("[data-assignment-participants]");
    if (!panel || panel.classList.contains("hidden")) return [];

    return checkedParticipantLaborUnitsInputs(panel).filter((input) => this.#visible(input));
  }

  // Jornales only have one answer when every task (consecutive run) has the same length.
  #daysFor(dates) {
    const lengths = consecutiveDateGroups(dates).map((group) => group.length);
    if (lengths.length === 0) return { count: 0, each: false, warning: false };

    const equal = lengths.every((length) => length === lengths[0]);
    return { count: equal ? lengths[0] : null, each: lengths.length > 1, warning: !equal };
  }

  #daysText(days) {
    if (days.count == null) return `? ${this.labels.dayOther}`;

    const text = `${days.count} ${days.count === 1 ? this.labels.dayOne : this.labels.dayOther}`;
    return days.each ? `${text} ${this.labels.each}` : text;
  }

  // --- lot facts -------------------------------------------------------------

  #renderFacts(container, state) {
    const available = !!(state.lot && state.factsTarget);
    container.classList.toggle("hidden", !available);
    container.querySelector("[data-lot-facts-label]").textContent = available ? `${state.lot.name}:` : "";

    container.querySelectorAll("[data-lot-fact]").forEach((element) => {
      const fact = element.dataset.lotFact;
      const value = available ? this.#factValue(state.lot, fact) : null;
      element.classList.toggle("hidden", value == null);
      if (value == null) return;

      const matches = fact === state.kind;
      const unit = fact === "trees" ? state.lot.noun : this.labels.area;
      const text = element.querySelector("[data-lot-fact-text]");
      text.textContent = `${this.numberFormat.format(value)} ${unit}`;
      text.classList.toggle("font-semibold", matches);
      text.classList.toggle("text-base-content", matches);
      text.classList.toggle("text-base-content/70", !matches);

      const applied = parseFloat(state.factsTarget.value) === value;
      const useButton = element.querySelector("[data-lot-fact-use]");
      useButton.disabled = applied;
      this.#markApplied(element, applied);
    });

    return available;
  }

  #factValue(lot, fact) {
    const value = Number(lot?.[fact]);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  // --- personas × días -------------------------------------------------------

  #renderPeople(container, state) {
    const target = state.laborUnitsTarget;
    container.classList.toggle("hidden", !target);
    if (!target) return false;

    // With a group the people count is the checked participants; otherwise the
    // user types it (the server renders "1"; an emptied field just shows "—").
    const input = container.querySelector("[data-people-input]");
    input.readOnly = target.perPerson;
    input.classList.toggle("bg-base-200", target.perPerson);
    if (target.perPerson) input.value = String(target.inputs.length);

    const total = this.#laborUnitsFor(this.#peopleCount(container, state), state.days);
    container.querySelector("[data-days-text]").textContent = this.#daysText(state.days);
    container.querySelector("[data-labor-units-text]").textContent = this.#laborUnitsText(total, state);
    container.querySelector("[data-days-warning]").classList.toggle("hidden", !state.days.warning);

    container
      .querySelector("[data-applied-icon]")
      .classList.toggle("hidden", !this.#laborUnitsApplied(state, total));

    return true;
  }

  #peopleCount(container, state) {
    if (state.laborUnitsTarget?.perPerson) return state.laborUnitsTarget.inputs.length;

    const count = parseInt(container.querySelector("[data-people-input]").value, 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  }

  #laborUnitsFor(people, days) {
    if (!days.count || people <= 0) return null;
    return people * days.count;
  }

  #laborUnitsText(total, state) {
    if (total == null) return "—";

    const unit = total === 1 ? this.labels.laborUnitOne : this.labels.laborUnits;
    let text = `${this.numberFormat.format(total)} ${unit}`;
    if (state.laborUnitsTarget.perPerson) {
      text += ` (${this.numberFormat.format(state.days.count)} ${this.labels.perPerson})`;
    }
    return text;
  }

  // What each target input receives: the days per person, or the total.
  #laborUnitsValue(state, total) {
    return state.laborUnitsTarget.perPerson ? state.days.count : total;
  }

  #laborUnitsApplied(state, total) {
    if (!state.laborUnitsTarget || total == null) return false;

    const value = this.#laborUnitsValue(state, total);
    return state.laborUnitsTarget.inputs.every((input) => parseFloat(input.value) === value);
  }

  // --- applied state / writing -----------------------------------------------

  // Applied = the item's leading icon gives way to the check icon rendered next to it.
  #markApplied(element, applied) {
    element.querySelector("[data-state-icon]").classList.toggle("hidden", applied);
    element.querySelector("[data-applied-icon]").classList.toggle("hidden", !applied);
  }

  // Dispatches `input` so line-item-form / line-item-rows update summaries and
  // mirrors, but only when the value really changes.
  #setValue(input, value) {
    const next = String(value);
    if (input.value === next) return;

    input.value = next;
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }
}
